import { extractReportIdFromResponse, sendReport } from "../../functions/sendReport";
import { getStoredUser } from "../../functions/themeAssets";
import { saveReportId } from "./gameApi";

/**
 * Keeps the external reports server in step with the game (the PHP game's addReport / updateReport):
 * a report is created when the first challenge opens (points 0) and updated after every challenge.
 * Demo players are never reported. Failures are logged and never stop the game.
 */

const roleOf = (user) => user.role || user.source;
const isGuest = (user) => roleOf(user) === "GUEST_USER";
const isDemo = (user) => roleOf(user) === "demobypass" || String(user.sessionId || "").startsWith("demo");

let pending = null;

function remember(reportId) {
  try {
    const stored = getStoredUser();
    if (stored) sessionStorage.setItem("userData", JSON.stringify({ ...stored, reportId: String(reportId) }));
  } catch (_) {
    /* ignore */
  }
}

const payload = (user, extra) => ({
  sessionId: user.sessionId,
  organizationId: user.organizationId,
  userId: user.userId,
  role: roleOf(user),
  token: user.token,
  gameId: user.gameId,
  name: user.name || "",
  ...extra,
});

/** Creates the report once (guests and registered players), remembering its id server side. */
export function startReport(user, knownReportId) {
  if (!user || isDemo(user)) return Promise.resolve(null);
  const existing = knownReportId || user.reportId;
  if (existing) return Promise.resolve(String(existing));
  if (pending) return pending;

  pending = (async () => {
    try {
      const res = await sendReport(payload(user, { points: 0, time: isGuest(user) ? "00:00:00" : "NA" }));
      const id = extractReportIdFromResponse(res);
      if (id) {
        remember(id);
        await saveReportId(user, id).catch((err) => console.error("setReportId failed", err));
      }
      return id;
    } catch (err) {
      console.error("Report create failed", err);
      return null;
    } finally {
      pending = null;
    }
  })();
  return pending;
}

/** Pushes the running score + time ("MM:SS") to the report. */
export async function updateReport(user, knownReportId, { points, time }) {
  if (!user || isDemo(user)) return;
  try {
    let reportId = knownReportId || getStoredUser()?.reportId || null;
    if (!reportId && pending) reportId = await pending;
    const res = await sendReport(payload(user, { points, time: `00:${time}`, reportId: reportId || undefined }));
    if (!reportId) {
      const id = extractReportIdFromResponse(res);
      if (id) {
        remember(id);
        await saveReportId(user, id).catch(() => {});
      }
    }
  } catch (err) {
    console.error("Report update failed", err);
  }
}
